import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, RefreshCw } from 'lucide-react';

export interface ModalShellProps {
  isOpen: boolean;
  onClose: () => void;
  title: React.ReactNode;
  badge?: React.ReactNode;
  badgeColor?: string;
  badgeBg?: string;
  subtitle?: React.ReactNode;
  icon?: React.ReactNode;
  iconBg?: string;
  iconColor?: string;
  iconStyle?: React.CSSProperties;
  maxWidth?: string | number;
  onRefresh?: () => Promise<void> | void;
  isRefreshing?: boolean;
  headerActions?: React.ReactNode;
  footerLeft?: React.ReactNode;
  footerRight?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  overlayClassName?: string;
  dialogClassName?: string;
  headerClassName?: string;
  bodyClassName?: string;
  footerClassName?: string;
  closeAriaLabel?: string;
  refreshAriaLabel?: string;
}

export const ModalShell: React.FC<ModalShellProps> = ({
  isOpen,
  onClose,
  title,
  badge,
  badgeColor = 'var(--ns-text-secondary, #475569)',
  badgeBg = 'var(--ns-surface-subtle, #f1f5f9)',
  subtitle,
  icon,
  iconBg = 'var(--ns-surface-subtle, #f1f5f9)',
  iconColor = 'var(--ns-text-secondary, #475569)',
  iconStyle,
  maxWidth = '1280px',
  onRefresh,
  isRefreshing = false,
  headerActions,
  footerLeft,
  footerRight,
  children,
  className = '',
  overlayClassName = '',
  dialogClassName = '',
  headerClassName = '',
  bodyClassName = '',
  footerClassName = '',
  closeAriaLabel = 'Close',
  refreshAriaLabel = 'Refresh',
}) => {
  useEffect(() => {
    if (!isOpen) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const modalNode = (
    <div
      className={`ns-modal-overlay ${overlayClassName}`}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="ns-modal-title"
    >
      <div
        className={`ns-modal-dialog ${dialogClassName} ${className}`}
        style={{ maxWidth }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`ns-modal-header ${headerClassName}`}>
          <div className="ns-modal-header__left">
            {icon && (
              <div
                className="ns-modal-header__icon-wrap ns-fiidii-modal__icon-wrap"
                style={{
                  background: iconBg,
                  color: iconColor,
                  ...(iconStyle || {}),
                }}
              >
                {icon}
              </div>
            )}
            <div>
              <div className="ns-modal-header__title-row" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <h2 id="ns-modal-title" className="ns-modal-header__title ns-fiidii-modal__title">
                  {title}
                </h2>
                {badge && (
                  React.isValidElement(badge) ? (
                    badge
                  ) : (
                    <span
                      className="ns-modal-header__badge"
                      style={{
                        background: badgeBg || 'var(--ns-surface-subtle, #f1f5f9)',
                        color: badgeColor || 'var(--ns-text-secondary, #475569)',
                        border: '1px solid var(--ns-border-default, #e2e8f0)',
                      }}
                    >
                      {badge}
                    </span>
                  )
                )}
              </div>
              {subtitle && <p className="ns-modal-header__subtitle ns-fiidii-modal__subtitle">{subtitle}</p>}
            </div>
          </div>

          <div className="ns-modal-header__actions ns-fiidii-modal__header-right">
            {headerActions}
            {onRefresh && (
              <button
                className="ns-modal-header__btn ns-fiidii-modal__refresh-btn"
                onClick={onRefresh}
                disabled={isRefreshing}
                title="Refresh latest institutional data"
                aria-label={refreshAriaLabel}
              >
                <RefreshCw
                  size={13}
                  className={isRefreshing ? 'animate-spin' : ''}
                  style={{
                    animation: isRefreshing ? 'spin 1s linear infinite' : 'none',
                  }}
                />
                <span className="ns-modal-header__btn-label">{isRefreshing ? 'Refreshing...' : 'Refresh'}</span>
              </button>
            )}
            <button
              className="ns-modal-header__btn ns-modal-header__btn--close ns-fiidii-modal__close-btn"
              onClick={onClose}
              aria-label={closeAriaLabel}
              title="Close (Esc)"
            >
              <X size={15} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className={`ns-modal-body ${bodyClassName}`}>{children}</div>

        {/* Footer */}
        {(footerLeft || footerRight) && (
          <div className={`ns-modal-footer ${footerClassName}`}>
            <div className="ns-modal-footer__left ns-fiidii-modal__footer-left">{footerLeft}</div>
            <div className="ns-modal-footer__right ns-fiidii-modal__footer-right">{footerRight}</div>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modalNode, document.body);
};

export default ModalShell;
